import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import { fetchSermons, fetchCategories } from "../lib/api";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";

export default function Sermoes({ kind = "sermon" }) {
  const isBriefStudy = kind === "brief_study";
  const [sermons, setSermons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories().then((r) => setCategories(r.categories || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { content_type: kind };
    if (q) params.q = q;
    if (cat) params.category = cat;
    const t = setTimeout(() => {
      fetchSermons(params)
        .then(setSermons)
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, cat, kind]);

  const listByCategory = useMemo(() => {
    const map = {};
    sermons.forEach((s) => {
      const k = s.category || "Outros";
      if (!map[k]) map[k] = [];
      map[k].push(s);
    });
    return map;
  }, [sermons]);

  return (
    <div className="max-w-5xl mx-auto section-padding py-8" data-testid="sermons-page" data-kind={kind}>
      <div className="mb-10">
        <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50">
          {isBriefStudy ? "Leitura rápida" : "Biblioteca pastoral"}
        </p>
        <h1 className="font-display text-4xl sm:text-5xl text-navy mt-2" data-testid="sermons-page-title">
          {isBriefStudy ? "Estudos Breves" : "Sermões"}
        </h1>
        <p className="text-navy/70 mt-3 max-w-2xl">
          {isBriefStudy
            ? "Meditações curtas com estrutura enxuta: texto bíblico, verdade central, aplicação e conclusão. Ideal para 3 a 5 minutos de leitura."
            : "Reflexões e pregações organizadas em estrutura homilética: tema, texto base, introdução, desenvolvimento, aplicação e conclusão."}
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-navy/50" />
        <Input
          data-testid="sermons-search"
          placeholder={isBriefStudy ? "Buscar por título, tema ou versículo..." : "Buscar por tema, título ou texto base..."}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-10 h-12 rounded-full bg-white border-border"
        />
      </div>

      {/* Categories */}
      {categories.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-2" data-testid="sermons-categories">
          <button
            onClick={() => setCat(null)}
            data-testid="sermons-cat-all"
            className={`text-xs px-4 py-1.5 rounded-full border transition ${
              !cat
                ? "bg-navy text-white border-navy"
                : "bg-white text-navy/70 border-border hover:border-navy/40"
            }`}
          >
            Todas
          </button>
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`sermons-cat-${c}`}
              onClick={() => setCat(c)}
              className={`text-xs px-4 py-1.5 rounded-full border transition ${
                cat === c
                  ? "bg-navy text-white border-navy"
                  : "bg-white text-navy/70 border-border hover:border-navy/40"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {/* List */}
      <div className="mt-10 space-y-10">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        ) : sermons.length === 0 ? (
          <p className="text-navy/60" data-testid="sermons-empty">
            {isBriefStudy
              ? "Ainda não temos Estudos Breves publicados. Volte em breve."
              : "Nenhum sermão encontrado."}
          </p>
        ) : (
          Object.entries(listByCategory).map(([catName, list]) => (
            <section key={catName}>
              <div className="flex items-center gap-3 mb-5">
                <span className="text-[10px] uppercase tracking-[0.3em] text-gold">{catName}</span>
                <span className="flex-1 h-px bg-border" />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {list.map((s) => (
                  <Link
                    key={s.id}
                    to={`${isBriefStudy ? "/estudos-breves" : "/sermoes"}/${s.id}`}
                    data-testid={`sermon-item-${s.id}`}
                    className="group block bg-white border border-border rounded-2xl p-5 card-lift"
                  >
                    <h3 className="font-display text-xl text-navy leading-snug">{s.title}</h3>
                    <p className="text-xs text-gold mt-2 tracking-wide">{s.base_text}</p>
                    <p className="mt-3 text-sm text-navy/70 line-clamp-2">{s.theme}</p>
                    <div className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/70 group-hover:text-gold">
                      {isBriefStudy ? "Ler estudo" : "Ler sermão"} <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}
